// Buscas da tela de Parceiros (#6498): a rede do parceiro (vendedores parceiros
// ligados a ele por `profiles.parceiro_id`), o parceiro dono de quem é vendedor
// parceiro, e os clientes de todos eles.
//
// A comissão vem da conta do "Meu Trabalho" (useResultadoMes + tarifa do mês):
// a tabela `comissao_parceiros` só é lida por adm, gerente e analista.
import { useEffect, useState } from 'react';
import { supabase } from '../../src/lib/supabase';
import type { Profile } from '../../types';
import type { ClienteDoParceiro } from '../../src/lib/visaoGeral/parceiros';

const PAGINA = 1000;

export interface RedeDoParceiro {
  /** Vendedores parceiros ativos da rede (só para o parceiro). */
  equipe: Profile[];
  /** Parceiro dono da rede (só para o vendedor parceiro). */
  dono: Pick<Profile, 'id' | 'full_name'> | null;
}

export function useRedeDoParceiro(pessoa: Profile): RedeDoParceiro | null {
  const [rede, setRede] = useState<RedeDoParceiro | null>(null);
  useEffect(() => {
    let vivo = true;
    setRede(null);
    (async () => {
      const [equipe, dono] = await Promise.all([
        pessoa.role === 'parceiro'
          ? supabase.from('profiles').select('*').eq('role', 'vendedor_parceiro').eq('parceiro_id', pessoa.id).eq('status', 'active').order('full_name')
          : Promise.resolve({ data: [], error: null }),
        pessoa.role === 'vendedor_parceiro' && (pessoa as any).parceiro_id
          ? supabase.from('profiles').select('id, full_name').eq('id', (pessoa as any).parceiro_id).maybeSingle()
          : Promise.resolve({ data: null, error: null }),
      ]);
      if (!vivo) return;
      setRede({ equipe: ((equipe.data || []) as Profile[]), dono: (dono.data as any) ?? null });
    })().catch(e => { console.error('Rede do parceiro:', e); if (vivo) setRede({ equipe: [], dono: null }); });
    return () => { vivo = false; };
  }, [pessoa]);
  return rede;
}

/** Negociações de hoje das pessoas (perdidas e reprovadas ficam de fora). */
export function useClientesDoParceiro(ids: string[] | null): { clientes: ClienteDoParceiro[] | null; erro: string | null } {
  const [estado, setEstado] = useState<{ clientes: ClienteDoParceiro[] | null; erro: string | null }>({ clientes: null, erro: null });
  const chave = ids ? [...ids].sort().join(',') : null;
  useEffect(() => {
    let vivo = true;
    setEstado({ clientes: null, erro: null });
    if (chave === null) return;
    (async () => {
      const todas: ClienteDoParceiro[] = [];
      for (let p = 0; ; p++) {
        const { data, error } = await supabase
          .from('negociacoes')
          .select('id, cliente_nome, status, consumo_medio_kwh, responsavel_id, url_termo, stage_changed_at, analise_cadastral(decisao_final, decisao_final_at)')
          .in('responsavel_id', chave.split(','))
          .not('status', 'in', '(perdido,termo_reprovado)')
          .order('id')
          .range(p * PAGINA, p * PAGINA + PAGINA - 1);
        if (error) throw error;
        // O embed 1→1 vem como objeto: a conta lê como lista.
        todas.push(...((data || []) as any[]).map(n => ({ ...n, analise_cadastral: n.analise_cadastral == null ? [] : [].concat(n.analise_cadastral) })));
        if (!data || data.length < PAGINA) break;
      }
      if (vivo) setEstado({ clientes: todas, erro: null });
    })().catch(e => { console.error('Clientes do parceiro:', e); if (vivo) setEstado({ clientes: [], erro: 'Não foi possível carregar os clientes.' }); });
    return () => { vivo = false; };
  }, [chave]);
  return estado;
}
